import { products, knownProducts, isProductEnabled } from '../config/products.js';
import { catalogDefaults } from '../config/defaults.js';
import { productAffinities } from '../config/affinities.js';
import { detectProductId } from './detect-product.js';
import { createTranslator, localizeProduct, normalizeLocale } from '../i18n/index.js';

function validateProductId(id, option, definitions = products) {
  if (!definitions.some((product) => product.id === id)) {
    throw new Error(`[PositiveNav] Unknown product in ${option}: ${String(id)}`);
  }
  return id;
}

function validateIds(value, option) {
  if (!Array.isArray(value)) {
    throw new TypeError(`[PositiveNav] ${option} must be an array of product IDs.`);
  }
  for (const id of value) validateProductId(id, option);
  return new Set(value);
}

/**
 * Resolve the catalog without a DOM. Hostname, defaults and affinities are internal test arguments.
 * An explicit currentProduct takes precedence over hostname detection.
 */
export function resolveCatalog(
  options = {},
  hostname = globalThis.location?.hostname ?? '',
  defaults = catalogDefaults,
  affinities = productAffinities,
) {
  const { connectedApps = [], hiddenProducts = [] } = options;
  const connected = validateIds(connectedApps, 'connectedApps');
  const hidden = new Set([
    ...validateIds(defaults.hiddenProducts, 'catalogDefaults.hiddenProducts'),
    ...validateIds(hiddenProducts, 'hiddenProducts'),
  ]);
  const defaultOrder = [...validateIds(defaults.order, 'catalogDefaults.order')];
  // Append any new product missing from the shared order.
  defaultOrder.push(...products.filter(({ id }) => !defaultOrder.includes(id)).map(({ id }) => id));
  const requestedCurrentId = options.currentProduct === undefined
    ? detectProductId(hostname)
    : validateProductId(options.currentProduct, 'currentProduct', knownProducts);
  const currentId = isProductEnabled(requestedCurrentId) ? requestedCurrentId : null;
  // Current-only hosts are included for the active label, never as other products.
  if (currentId && !defaultOrder.includes(currentId)) defaultOrder.push(currentId);
  const preferred = currentId && Object.prototype.hasOwnProperty.call(affinities, currentId)
    ? affinities[currentId]
    : undefined;
  // Promote other products, then complete the list with the shared default order.
  const priorityIds = [...validateIds(preferred === undefined ? [] : preferred, `productAffinities.${currentId}`)]
    .filter((id) => id !== currentId);
  const order = [...new Set([...priorityIds, ...defaultOrder])];
  const locale = normalizeLocale(options.locale);
  const t = createTranslator(locale);

  const visibleProducts = Object.freeze(order
    .filter((id) => isProductEnabled(id) && (id === currentId || !hidden.has(id)))
    .map((id) => {
      const product = knownProducts.find((product) => product.id === id);
      return Object.freeze({
        ...product,
        ...localizeProduct(product, t),
        urls: product.localizedDiscoverUrls?.[locale]
          ? Object.freeze({ ...product.urls, discover: product.localizedDiscoverUrls[locale] })
          : product.urls,
        isCurrent: id === currentId,
        isConnected: connected.has(id),
        status: id === currentId ? 'current': connected.has(id) ? 'connected': 'discover',
      });
    }));
  const currentProduct = visibleProducts.find(({ isCurrent }) => isCurrent) ?? null;
  const otherProducts = visibleProducts.filter(({ isCurrent }) => !isCurrent);
  const connectedProducts = Object.freeze(otherProducts.filter(({ isConnected }) => isConnected));
  const discoverProducts = Object.freeze(otherProducts.filter(({ isConnected }) => !isConnected));

  return Object.freeze({
    currentProduct,
    visibleProducts,
    connectedProducts,
    discoverProducts,
    counts: Object.freeze({
      visible: visibleProducts.length,
      other: otherProducts.length,
      connected: connectedProducts.length,
      discover: discoverProducts.length,
    }),
  });
}
